import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';


interface GridColumn {
  field: string;
  title: string;
  width?: string;
  type: 'text' | 'currency' | 'tax' | 'taxCode';
  groupBy?: boolean;
}

interface AggregatedGridRow {
  [key: string]: any;
  taxes: any[];
}

interface DataChangeEvent {
  tenantId: number;
 // serviceCategoryId: number;
  serviceCategoryName: string;
  field: string;
  value: any;
}

@Component({
  selector: 'app-aggregated-data-grid',
  templateUrl: './aggregated-data-grid.component.html',
  styleUrls: ['./aggregated-data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputNumberModule,
    SelectModule,
    InputTextModule,
    TranslateModule,
    ButtonModule,
    StyleClassModule,
    RippleModule
    ]
})
export class AggregatedDataGridComponent implements OnInit, OnDestroy {
  @Input() columns = new BehaviorSubject<GridColumn[]>([]);
  @Input() data = new BehaviorSubject<AggregatedGridRow[]>([]);
  @Input() loading = false;
  @Input() headerClasses = 'surface-100 font-bold border-none white-space-nowrap uppercase';
  @Input() tableClasses = 'p-0 shadow-2 my-3';
  @Input() groupedTaxes: any[] = [];
  @Input() isEditMode: boolean = false;
  @Output() onDataChange = new EventEmitter<DataChangeEvent>();
  @Output() editPlanDetails = new EventEmitter<void>();

  private subscriptions: Subscription[] = [];
  private isInternalUpdate = false;

  gridForm = this.fb.group({
    rows: this.fb.array<FormGroup>([])
  });

  // Form controls for apply all
  applyAllForm = this.fb.group({
    selectedTax: [null],
    price: [''],
    taxCode: ['']
  });

  // Add a Map to track messages for each row
  customTaxMessages = new Map<string, string>();

  selectedTenants: any[] = [];

  constructor(
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.columns.subscribe(() => this.cdr.markForCheck()),
      this.data.subscribe(data => {
        if (!this.isInternalUpdate) {
          this.initializeForm(data);
        }
        this.cdr.markForCheck();
      }),
      this.gridForm.valueChanges.subscribe(value => {
        if (!this.isInternalUpdate) {
          this.handleFormChanges(value);
        }
      }),
      // this.data.subscribe(data => {
      //   if (data.length > 0) {
      //     this.availableTaxes = data[0].availableTaxes || [];
      //   }
      // })
    );
  }

  private initializeForm(data: AggregatedGridRow[]) {
    const formArray = this.fb.array<FormGroup>([]);
    
    data.forEach(row => {
      const rowGroup = this.fb.group({
        tenantId: [row.tenantId],
        tenantName: [row.tenantName],
        //serviceCategoryId: [row.serviceCategoryId],
        serviceCategoryName: [row.serviceCategoryName],
        price: [row.price],
        selectedTax: [row.selectedTax],
        taxCode: [row.taxCode],
        isRecurring: [row.isRecurring],
        availableTaxes: [row.availableTaxes]
      });
      formArray.push(rowGroup);
    });

    this.gridForm.setControl('rows', formArray);
  }

  private handleFormChanges(value: any) {
    this.isInternalUpdate = true;
    try {
      // Find which control changed
      const changedRow = value.rows.find((row: any, index: number) => {
        const currentData = this.data.getValue()[index];
        return Object.keys(row).some(key => row[key] !== currentData[key]);
      });

      if (changedRow) {
        // Find which field changed
        const changedField = Object.keys(changedRow).find(key => {
          const currentData = this.data.getValue().find(row => 
            row.tenantId === changedRow.tenantId && 
            row.serviceCategoryName === changedRow.serviceCategoryName
          );
          return currentData && changedRow[key] !== currentData[key];
        });

        if (changedField) {
          // Emit in the format expected by parent
          this.onDataChange.emit({
            tenantId: changedRow.tenantId,
            serviceCategoryName: changedRow.serviceCategoryName,
            //serviceCategoryId: changedRow.serviceCategoryId,
            field: changedField,
            value: changedRow[changedField]
          });
        }
      }
    } finally {
      this.isInternalUpdate = false;
    }
  }

  get rowControls(): FormGroup[] {
    return (this.gridForm.get('rows') as FormArray).controls as FormGroup[];
  }

  isGroupStart(rowIndex: number, data: AggregatedGridRow[]): boolean {
    if (rowIndex === 0) return true;
    return data[rowIndex].tenantId !== data[rowIndex - 1].tenantId;
  }

  getRowSpan(rowIndex: number, data: AggregatedGridRow[]): number {
    const currentTenantId = data[rowIndex].tenantId;
    let span = 0;
    for (let i = rowIndex; i < data.length && data[i].tenantId === currentTenantId; i++) {
      span++;
    }
    return span;
  }

  // Apply to all methods
  applyTaxToAll() {
    var selectedTax = this.applyAllForm.get('selectedTax')!.value as any;
    if (selectedTax) {
      const rows = this.gridForm.get('rows') as FormArray;
      
      rows.controls.forEach((control: AbstractControl) => {
        if (control instanceof FormGroup) {
          const availableTaxes = control.get('availableTaxes')?.value || [];
          const rowId = `${control.get('tenantId')?.value}-${control.get('serviceCategoryName')?.value}`;
          
          // Check if selected tax exists in available taxes
          const taxExists = availableTaxes.some((tax: any) => tax.key === selectedTax.key);
          
          if (!taxExists) {
            
            var updatedTaxes = [...availableTaxes, selectedTax];
            control.patchValue({ 
              availableTaxes: updatedTaxes
            }, { emitEvent: true });

            control.controls['availableTaxes'].setValue(updatedTaxes);
            this.cdr.detectChanges();

            control.patchValue({ 
              selectedTax: selectedTax
            }, { emitEvent: true });

            control.controls['selectedTax'].setValue(selectedTax);
            control.updateValueAndValidity();

            // Set message for this specific row
            this.customTaxMessages.set(rowId, `Added custom tax "${selectedTax.key}"`);
            this.cdr.detectChanges();
          } else {
            // Just set the selected tax
            control.patchValue({ 
              selectedTax: selectedTax 
            }, { emitEvent: true });
            
            // Clear any existing message
            this.customTaxMessages.delete(rowId);
          }
        }
      });
    }
  }

  applyPriceToAll() {
    const price = this.applyAllForm.get('price')?.value;
    if (price) {
      const rows = this.gridForm.get('rows') as FormArray;
      rows.controls.forEach((control: AbstractControl) => {
        if (control instanceof FormGroup) {
          control.patchValue({ price: parseFloat(price) }, { emitEvent: true });
        }
      });
    }
  }

  applyTaxCodeToAll() {
    const taxCode = this.applyAllForm.get('taxCode')?.value;
    if (taxCode) {
      const rows = this.gridForm.get('rows') as FormArray;
      rows.controls.forEach((control: AbstractControl) => {
        if (control instanceof FormGroup) {
          control.patchValue({ taxCode }, { emitEvent: true });
        }
      });
    }
  }

  // Add helper method to get message for a specific row
  getCustomTaxMessage(tenantId: number, serviceCategoryName: number): string {
    return this.customTaxMessages.get(`${tenantId}-${serviceCategoryName}`) || '';
  }

  onEditPlanDetails() {
    this.editPlanDetails.emit();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
} 